import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { values } from "lodash-es";
import { PureComponent } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { CalloutIntent } from "widgets";

import { simpleFieldValidation, specialCharacterValidation } from "../../app-utils/form.js";
import { getIconForPlaybookCategory } from "../../app-utils/playbook.js";
import { type Attribute, type InputPlaybookCategoryInput, type PlaybookCategory } from "../../graphql.js";
import { modifyAdminPlaybookCategory, saveAdminPlaybookCategory } from "../../redux/modules/playbook-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { PlaybookCategoriesIcons, type PlaybookCategoryIcon } from "../../settings/assets-settings.js";
import { Dialog, type IPopoverProps } from "../core/overlays/Dialogs.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";

interface IPlaybookCategoryEditDialogProps {
  playbookCategory: PlaybookCategory;
  attributeCreated?: (createdAttribute: Attribute) => void;
  onClose: () => void;
  attributeToEdit?: Attribute;
}

interface IPlaybookCategoryEditDialogAppStateProps {
  initialValues: Partial<FormData>;
}

interface IPlaybookCategoryEditDialogDispatchProps {
  modifyCategory: (payload: InputPlaybookCategoryInput) => Promise<PlaybookCategory>;
  createCategory: (payload: InputPlaybookCategoryInput) => Promise<PlaybookCategory>;
}

interface IPlaybookCategoryEditDialogAllProps
  extends IPlaybookCategoryEditDialogProps,
    IPlaybookCategoryEditDialogAppStateProps,
    IPlaybookCategoryEditDialogDispatchProps,
    InjectedFormProps<FormData, any> {}

function iconOptionRenderer(icon: PlaybookCategoryIcon) {
  return <img src={icon.large} alt="" width={50} height={50} style={{ borderRadius: 25 }} />;
}

function mapStateToProps(
  _state: IAppState,
  ownProps: IPlaybookCategoryEditDialogProps,
): IPlaybookCategoryEditDialogAppStateProps {
  return {
    initialValues: {
      [PlaybookCategoryEditDialogImpl.CATEGORY_NAME]: ownProps.playbookCategory?.name,
      [PlaybookCategoryEditDialogImpl.CATEGORY_IMAGE]: getIconForPlaybookCategory(ownProps.playbookCategory),
    },
  };
}

function mapDispatchToProps(dispatch): IPlaybookCategoryEditDialogDispatchProps {
  return {
    modifyCategory: (payload: InputPlaybookCategoryInput) => dispatch(modifyAdminPlaybookCategory(payload)),
    createCategory: (payload: InputPlaybookCategoryInput) => dispatch(saveAdminPlaybookCategory(payload)),
  };
}

class PlaybookCategoryEditDialogImpl extends PureComponent<IPlaybookCategoryEditDialogAllProps> {
  public static ADD_PLAYBOOK_CATEGORY_DIALOG_FORM_NAME = "addPlaybookCategory";
  public static CATEGORY_NAME = "categoryName";
  public static CATEGORY_IMAGE = "categoryImage";
  public static CATEGORY_ICONS: PlaybookCategoryIcon[] = values(PlaybookCategoriesIcons);

  constructor(props: IPlaybookCategoryEditDialogAllProps) {
    super(props);
  }

  handleSubmitData = (values): Promise<any> => {
    const payload: InputPlaybookCategoryInput = {
      id: this.props.playbookCategory?.id,
      name: values[PlaybookCategoryEditDialogImpl.CATEGORY_NAME],
      image: values[PlaybookCategoryEditDialogImpl.CATEGORY_IMAGE]?.id,
    };

    const storageFunction: any = this.props.playbookCategory ? this.props.modifyCategory : this.props.createCategory;

    return storageFunction(payload).then(
      (category: PlaybookCategory) => {
        this.props.onClose();
        this.props.reset();
        return category;
      },
      // the following will throw an error that redux-form understands as a submission error, with a general error that will be
      // available from this.props.error
      (rejected: Error) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  render() {
    let footerMessage: IPopoverProps["footerMessage"];
    if (this.props.error) {
      footerMessage = { message: this.props.error };
    } else if (this.props.warning) {
      footerMessage = { message: this.props.warning, intent: CalloutIntent.Warning };
    }

    return (
      <Dialog
        mainActionButtonProps={{
          children: "Save",
          isLoading: this.props.submitting,
          isDisabled: this.props.submitting,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        headerProps={{ title: this.props.playbookCategory ? "Edit Playbook Category" : "New Playbook Category" }}
        closeFn={this.props.onClose}
        footerMessage={footerMessage}
      >
        <div>
          <InputReduxFormField
            componentClass="input"
            name={PlaybookCategoryEditDialogImpl.CATEGORY_NAME}
            label="Name"
            placeholder="Enter a category name"
            maxLength={DB_FIELD_MAX_LENGTH.PLAYBOOKS.CATEGORY_NAME}
            component={InputField}
            validate={[simpleFieldValidation, specialCharacterValidation]}
            horizontalLayout={false}
          />
          <ReactSelectReduxFormField
            key={PlaybookCategoryEditDialogImpl.CATEGORY_IMAGE}
            name={PlaybookCategoryEditDialogImpl.CATEGORY_IMAGE}
            horizontalLayout={false}
            isClearable={false}
            isSearchable={false}
            label={"Image"}
            component={ReactSelectField}
            formatOptionLabel={iconOptionRenderer}
            options={PlaybookCategoryEditDialogImpl.CATEGORY_ICONS}
            getOptionValue={(option: PlaybookCategoryIcon) => option.id}
            getOptionLabel={(option: PlaybookCategoryIcon) => option.large}
            isDisabled={false}
            customStyles={{
              valueContainer: (provided) => ({
                ...provided,
                height: "70px",
              }),
              container: (provided) => ({
                ...provided,
                width: "110px",
              }),
            }}
            maxMenuHeight={400}
          />
        </div>
      </Dialog>
    );
  }
}

const PlaybookCategoryEditDialog = connect<
  IPlaybookCategoryEditDialogAppStateProps,
  IPlaybookCategoryEditDialogDispatchProps,
  IPlaybookCategoryEditDialogProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: PlaybookCategoryEditDialogImpl.ADD_PLAYBOOK_CATEGORY_DIALOG_FORM_NAME,
  })(PlaybookCategoryEditDialogImpl),
);

export { PlaybookCategoryEditDialog };
export default PlaybookCategoryEditDialog;
